import { setParentHeight } from "./utils";
import { useEffect, useState } from "react";

export default function useAutosize({
  dontResizeOnZeroHeight = false,
  enabled = true,
  isShown,
  label,
}: {
  dontResizeOnZeroHeight?: boolean;
  enabled?: boolean;
  isShown?: boolean;
  label?: string;
}) {
  const [elementRef, setElementRef] = useState<HTMLElement | null>(null);

  // Keep track of whether we have been displayed before.
  // Prevents a component from resizing everything down to document.body size on first render if
  // a dialog is already open.
  const [pastState, setPastState] = useState(false);

  useEffect(() => {
    document.frameResizingForElement = enabled;
  }, [enabled]);

  useEffect(() => {
    let resizeTimeout;
    const resizeObs = new ResizeObserver((entries) => {
      if (!enabled || document.frameResizingForPopup) return;
      if (resizeTimeout) clearTimeout(resizeTimeout);

      // A slight debounce to stop the frame from jumping up and down
      // when a page/popup fills in initially.
      resizeTimeout = setTimeout(() => {
        const cr = entries[0].contentRect;
        if (cr.height && cr.height > 250) {
          setPastState(true);
          setParentHeight(cr.height);
        } else if (pastState && !dontResizeOnZeroHeight) {
          setParentHeight(document.body.clientHeight);
          setPastState(false);
        }
      }, 100);
    });

    if (elementRef) resizeObs.observe(elementRef);
    else if (!dontResizeOnZeroHeight) resizeObs.observe(document.body);
  }, [elementRef, pastState, isShown]);

  return [setElementRef];
}

export function useAutosizeDocument() {
  useEffect(() => {
    const resizeObs = new ResizeObserver((entries) => {
      if (document.frameResizingForPopup || document.frameResizingForElement)
        return;
      const cr = entries[0].contentRect;
      setParentHeight(cr.height);
    });
    resizeObs.observe(document.body);
  }, []);
}

export function useAdjustFrameForPopup({
  enabled = true,
  isShown,
  popupQuery,
  extraHeight = 10,
  label,
  fullPage = false,
  useInterval = false,
}: {
  enabled?: boolean;
  isShown: boolean | number | string | undefined;
  label?: string;
  popupQuery: string;
  extraHeight?: number;
  fullPage?: boolean;
  useInterval?: boolean;
}) {
  const [intervalVal, setIntervalVal] = useState<number | undefined>(undefined);

  function resize() {
    let popupElem = document.querySelector(popupQuery);
    if (!popupElem) return;

    let popupHeight = popupElem.getBoundingClientRect().height + extraHeight;

    let totalHeight = popupElem.getBoundingClientRect().top + popupHeight;
    fullPage
      ? setParentHeight(Math.max(popupHeight, document.body.clientHeight))
      : setParentHeight(Math.max(totalHeight, document.body.clientHeight));
  }

  useEffect(() => {
    if (!enabled) return;

    const documentHeight = document.body.getBoundingClientRect().height;
    if (isShown) {
      document.frameResizingForPopup = true;
      if (useInterval) {
        let id = setInterval(resize, 100);
        setIntervalVal(id);
      } else {
        resize();
      }
    } else {
      document.frameResizingForPopup = false;
      setParentHeight(documentHeight);
      if (useInterval) {
        clearInterval(intervalVal);
        setIntervalVal(undefined);
      }
    }
  }, [isShown]);
}
